import type { LineupPack } from "./channel-lineup.types";

/** The Genre Packs view's two filters (GP10, GP15) — pure, no React, no hook. */

/**
 * The slice of SR11's seam these filters read (`lineup-search.shared.ts`'s
 * `LineupSearchSelection`). De-namespaced CCIDs and Epoch plan codes, already expanded through
 * ZP12, so they are compared to `LineupChannel.id` and `LineupSubscription.id` directly.
 */
export interface GenrePackSearchIds {
  channelIds: readonly string[];
  subscriptionIds: readonly string[];
}

/**
 * GP15, first half — "a pack with no channels is not shown while browsing". Channels only,
 * deliberately: a pack carrying subscriptions but no channels is hidden here.
 */
export function browsableGenrePacks(packs: readonly LineupPack[]): LineupPack[] {
  return packs.filter(pack => pack.channels.length > 0);
}

/**
 * GP10 — "each pack shows only its matching channels and subscriptions; packs with no match are
 * hidden" — and GP15's second half, which is the same pass: a pack whose only match is a
 * subscription keeps it, with `channels` emptied, and `genre-pack.tsx` then draws just the
 * Subscriptions row (`logo-grid.tsx` renders nothing for an empty set).
 */
export function searchedGenrePacks(
  packs: readonly LineupPack[],
  { channelIds, subscriptionIds }: GenrePackSearchIds
): LineupPack[] {
  const wantedChannels = new Set(channelIds);
  const wantedSubscriptions = new Set(subscriptionIds);
  const shown: LineupPack[] = [];

  for (const pack of packs) {
    const channels = pack.channels.filter(channel => wantedChannels.has(channel.id));
    const subscriptions = pack.subscriptions.filter(
      subscription => wantedSubscriptions.has(subscription.id)
    );
    if (channels.length === 0 && subscriptions.length === 0) {
      continue;
    }
    /* A new pack object, never a mutation: `packs` is the resolver's array and
       the browse view holds the same objects. */
    shown.push({
      ...pack,
      channels,
      subscriptions
    });
  }

  return shown;
}
